/** @jsx jsx */
import React, { Fragment, useCallback } from 'react';
import { jsx } from '@emotion/core';
import { filter } from 'ramda';
import { format, parse, startOfDay } from 'date-fns/fp';

import { Grid, InputLabel } from '@material-ui/core';
import { InstantCredit } from 'state/instantCredit';
import { Tenant } from 'state/tenant';

import useTranslate from 'hooks/useTranslate';

import styles from '../styles';

interface DSPAttributePropTypes {
  label: string;
  value?: string | null | 0;
  dataCy?: string;
}

const DSPAttributeItem: React.FC<DSPAttributePropTypes> = ({
  label,
  value,
  dataCy,
}) => {
  return value ? (
    <Grid data-cy={dataCy} item xs={12} sm={6}>
      <InputLabel shrink>{label}</InputLabel>

      <p css={styles.dspValue}>{value}</p>
    </Grid>
  ) : null;
};

interface CardDetailsPropTypes {
  instantCredit: InstantCredit;
  tenant: Tenant;
}

/**
 * Card details from the InstantCredit response, displayed according to the
 * tspDisplay configuration.
 *
 * Must be inside a Container.
 */
const CardDetails: React.FC<CardDetailsPropTypes> = ({
  instantCredit,
  tenant,
}) => {
  const t = useTranslate();
  const { tspDisplay: show } = tenant;
  const {
    accountNumber,
    firstName,
    lastName,
    expirationDate,
    securityCode,
  } = instantCredit;

  const hasVirtualCard = Boolean(securityCode);
  const labelFor = useCallback(key => t(`dsp.dspContent.cardDetails.${key}`), [
    t,
  ]);

  const formatExpirationDate = (date: string) => {
    const parseFormat = 'Myyyy';
    const dateFormat = 'MM/yyyy';

    return format(
      dateFormat,
      startOfDay(parse(startOfDay(new Date()), parseFormat, date)),
    );
  };

  return (
    <Fragment>
      {show.accountNumber && !hasVirtualCard && accountNumber ? (
        <DSPAttributeItem
          key={labelFor('accountNumber')}
          label={labelFor('accountNumber')}
          value={accountNumber}
          dataCy={'accountNumber'}
        />
      ) : null}
      {show.temporaryAccountNumber && hasVirtualCard && accountNumber ? (
        <DSPAttributeItem
          key={labelFor('temporaryAccountNumber')}
          label={labelFor('temporaryAccountNumber')}
          value={accountNumber}
          dataCy={'temporaryAccountNumber'}
        />
      ) : null}
      {show.nameOnCard && firstName && lastName ? (
        <DSPAttributeItem
          key={labelFor('nameOnCard')}
          label={labelFor('nameOnCard')}
          value={filter(Boolean)([firstName, lastName]).join(' ')}
          dataCy={'nameOnCard'}
        />
      ) : null}
      {show.temporaryExpirationDate && expirationDate ? (
        <DSPAttributeItem
          key={labelFor('temporaryExpirationDate')}
          label={labelFor('temporaryExpirationDate')}
          value={formatExpirationDate(expirationDate)}
          dataCy={'temporaryExpirationDate'}
        />
      ) : null}
      {show.temporarySecurityCode && securityCode ? (
        <DSPAttributeItem
          key={labelFor('temporarySecurityCode')}
          label={labelFor('temporarySecurityCode')}
          value={securityCode}
          dataCy={'temporarySecurityCode'}
        />
      ) : null}
    </Fragment>
  );
};

export default CardDetails;
